import React, { useState } from 'react';
import { UsePromptDialog } from './UsePromptDialog';
import { ConfirmationDialog } from './ConfirmationDialog';
import { copyToClipboard } from '../../lib/clipboard';
import { openAiAgent, AI_AGENTS, AgentId } from '../../lib/external-urls';
import { toast } from 'sonner';
import { api } from '../../services/api';

export interface UsePromptHandlerProps {
  promptId: number;
  promptTitle: string;
  promptText: string;
  onUsageTracked?: () => void;
  children: (props: { onUsePrompt: () => void }) => React.ReactNode;
}

export const UsePromptHandler: React.FC<UsePromptHandlerProps> = ({
  promptId,
  promptTitle,
  promptText,
  onUsageTracked,
  children
}) => {
  const [showUseDialog, setShowUseDialog] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState<AgentId | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const handleUsePrompt = () => {
    setShowUseDialog(true);
  };

  const handleCloseUseDialog = () => {
    setShowUseDialog(false);
  };

  const trackUsage = async () => {
    try {
      const response = await api.incrementUses(promptId);
      console.log('Usage tracked successfully:', response);
      onUsageTracked?.();
    } catch (error) {
      console.error('Failed to track usage:', error);
    }
  };

  const handleUseWithAgent = async (agent: 'claude' | 'copilot' | 'chatgpt' | 'clipboard') => {
    if (isProcessing) return;
    
    setIsProcessing(true);
    setShowUseDialog(false);

    try {
      // Copy to clipboard first
      const clipboardResult = await copyToClipboard(promptText);
      
      if (!clipboardResult.success) {
        toast.error(`Failed to copy prompt: ${clipboardResult.error}`);
        setIsProcessing(false);
        return;
      }

      // Track usage
      await trackUsage();

      if (agent === 'clipboard') {
        // Just clipboard - show success and we're done
        toast.success('Prompt copied to clipboard!');
        setIsProcessing(false);
        return;
      }

      // For AI agents, show confirmation dialog
      setSelectedAgent(agent);
      setShowConfirmDialog(true);
      setIsProcessing(false);

    } catch (error) {
      console.error('Error processing prompt use:', error);
      toast.error('An error occurred while processing your request');
      setIsProcessing(false);
    }
  };

  const handleConfirmOpenAgent = () => {
    if (!selectedAgent) return;

    const result = openAiAgent(selectedAgent);
    
    if (result.success) {
      toast.success(`Opening ${AI_AGENTS[selectedAgent].name}...`);
    } else {
      toast.error(`Failed to open ${AI_AGENTS[selectedAgent].name}: ${result.error}`);
    }

    setShowConfirmDialog(false);
    setSelectedAgent(null);
  };

  const handleCloseConfirmDialog = () => {
    setShowConfirmDialog(false);
    setSelectedAgent(null);
  };

  return (
    <>
      {children({ onUsePrompt: handleUsePrompt })}
      
      <UsePromptDialog
        isOpen={showUseDialog}
        onClose={handleCloseUseDialog}
        promptTitle={promptTitle}
        promptText={promptText}
        onUseWithAgent={handleUseWithAgent}
      />

      {selectedAgent && (
        <ConfirmationDialog
          isOpen={showConfirmDialog}
          onClose={handleCloseConfirmDialog}
          onConfirm={handleConfirmOpenAgent}
          agentName={AI_AGENTS[selectedAgent].name}
          agentUrl={AI_AGENTS[selectedAgent].displayUrl}
        />
      )}
    </>
  );
};